import { useState } from "react";
import { Shield, Trash2, RotateCcw, Wand2, AlertTriangle, PlusCircle, HelpCircle, Loader2, Sparkles, ScrollText, EyeOff, Search, ExternalLink } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AutoResizeTextarea from "@/components/AutoResizeTextarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BACKEND_URL, uid, CAUTIONARY_TALES, PROTECTION_TYPES, OWNERSHIP_CONTROL, RISK_LEVELS, SCORE_LEVELS, SCORE_DIMENSIONS, SEVERITY_STYLES } from "./constants";

// ---- Inlined atoms ----
function FieldWand({ tab, field, fieldLabel, current, context, onAnswer, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/technology/suggest-field`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, field, fieldLabel, current, context }),
      });
      if (res.ok) { const d = await res.json(); if (d.answer) { onAnswer(d.answer); toast.success("AI draft ready"); } else toast.info("No draft"); }
      else toast.error("Failed"); } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  return <Button size="sm" variant="ghost" onClick={run} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={testid}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI draft</span></Button>;
}
function WandField({ tab, field, label, value, onChange, onBlur, rows = 3, placeholder, context, testid }) {
  return (<div>
    <div className="flex items-center justify-between"><Label className="text-sm font-medium text-slate-800">{label}</Label>
      <FieldWand tab={tab} field={field} fieldLabel={label} current={value} context={context} onAnswer={(a) => onChange(a)} testid={`${testid}-wand`} /></div>
    <AutoResizeTextarea value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} minRows={rows} maxHeight={560} className="mt-1 text-sm leading-relaxed" placeholder={placeholder} data-testid={testid} /></div>);
}
function CautionaryTale({ tale, testid }) {
  return (<Card className="border-amber-200 bg-amber-50 sticky top-4" data-testid={testid}>
    <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Cautionary tale · {tale.title}</CardTitle></CardHeader>
    <CardContent className="space-y-2 text-xs text-amber-900"><p>{tale.body}</p><p className="font-semibold">Lesson: <span className="font-normal">{tale.lesson}</span></p></CardContent></Card>);
}
function GuidancePanel({ warnings, onDismiss, onRefresh, busy }) {
  if (!warnings || !warnings.length) return null;
  return (<Card className="border-teal-200 bg-teal-50/50" data-testid="def-guidance">
    <CardHeader className="pb-2"><div className="flex items-center justify-between">
      <CardTitle className="text-sm text-teal-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Strategy coach warnings</CardTitle>
      <Button size="sm" variant="ghost" onClick={onRefresh} disabled={busy} className="h-7 px-2 text-teal-700" data-testid="def-guidance-refresh">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">Re-check</span></Button></div></CardHeader>
    <CardContent className="space-y-2">{warnings.map((w) => (
      <div key={w.id} className={`text-xs border px-2 py-1.5 rounded flex items-start gap-2 ${SEVERITY_STYLES[w.severity] || SEVERITY_STYLES.medium}`} data-testid={`def-warn-${w.id}`}>
        <Badge variant="outline" className="text-[10px] capitalize shrink-0">{w.severity}</Badge>
        <span className="flex-1 leading-snug">{w.message}</span>
        <button onClick={() => onDismiss(w.id)} className="opacity-60 hover:opacity-100" data-testid={`def-warn-del-${w.id}`}><Trash2 className="w-3 h-3" /></button></div>))}</CardContent></Card>);
}
function KeyQuestionsSection({ tab, tabData, questions, onSetQuestions, persistTab, testidBase }) {
  const [busy, setBusy] = useState(false); const [input, setInput] = useState("");
  const suggest = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/technology/suggest-key-questions`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ tab, tab_data: tabData, existing: questions.map((q) => q.text) }) });
      if (res.ok) { const d = await res.json(); const next = [...questions, ...(d.questions || []).map((t) => ({ id: uid(), text: t }))]; onSetQuestions(next); persistTab(next); if (d.questions?.length) toast.success(`Added ${d.questions.length}`); } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  const add = () => { if (!input.trim()) return; const next = [...questions, { id: uid(), text: input.trim() }]; onSetQuestions(next); persistTab(next); setInput(""); };
  const remove = (id) => { const next = questions.filter((q) => q.id !== id); onSetQuestions(next); persistTab(next); };
  return (<Card data-testid={`${testidBase}-section`}>
    <CardHeader className="pb-2"><div className="flex items-center justify-between">
      <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-teal-600" /> Key questions</CardTitle>
      <Button size="sm" variant="ghost" onClick={suggest} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={`${testidBase}-suggest`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI suggest</span></Button>
    </div></CardHeader>
    <CardContent className="space-y-2">{questions.length === 0 && <p className="text-xs text-slate-400 italic">No questions yet.</p>}{questions.map((q) => (
      <div key={q.id} className="flex items-start gap-2 border-l-2 border-l-teal-300 pl-3 py-1.5" data-testid={`${testidBase}-q-${q.id}`}><span className="text-sm text-slate-700 flex-1">{q.text}</span><button onClick={() => remove(q.id)} className="text-slate-300 hover:text-red-500" data-testid={`${testidBase}-q-del-${q.id}`}><Trash2 className="w-3.5 h-3.5" /></button></div>))}
      <div className="flex gap-2 pt-2 border-t"><Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add your own question…" className="text-sm h-9" data-testid={`${testidBase}-input`} /><Button size="sm" onClick={add} data-testid={`${testidBase}-add`}><PlusCircle className="w-4 h-4" /></Button></div></CardContent></Card>);
}

// ---- Espacenet FTO + Competitor watch-list ----
function EspacenetSection({ data }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(data?.espacenet_suggestions || { fto_searches: [], watchlist: [] });

  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/technology/espacenet-suggest`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ progress: data }),
      });
      if (res.ok) {
        const d = await res.json();
        setResult({ fto_searches: d.fto_searches || [], watchlist: d.watchlist || [] });
        toast.success(`Loaded ${(d.fto_searches || []).length} searches + ${(d.watchlist || []).length} to watch`);
      } else toast.error("Failed to generate suggestions");
    } catch (e) { toast.error("Failed to generate suggestions"); }
    setBusy(false);
  };

  const hasContent = result.fto_searches?.length > 0 || result.watchlist?.length > 0;

  return (
    <Card data-testid="def-espacenet-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2">
              <Search className="w-4 h-4 text-teal-600" /> Espacenet FTO & competitor IP watch
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Targeted Espacenet deep-link searches for freedom-to-operate + a shortlist of competitor patent portfolios worth watching.</p>
          </div>
          <Button size="sm" onClick={run} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white h-8" data-testid="def-espacenet-run">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
            {hasContent ? "Regenerate" : "AI-suggest searches"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {!hasContent && !busy && (
          <p className="text-xs text-slate-400 italic py-3 text-center border-2 border-dashed border-slate-200 rounded-lg">
            Fill out the Technology + IP Map above first, then click <span className="font-medium">AI-suggest searches</span>. The AI will generate 4–5 Espacenet CQL queries + a competitor watch-list based on your inputs.
          </p>
        )}
        {result.fto_searches?.length > 0 && (
          <div>
            <h4 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Freedom-to-operate + landscape searches</h4>
            <div className="space-y-2">
              {result.fto_searches.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="block border rounded-lg p-3 hover:border-teal-400 hover:shadow-sm transition-all bg-white" data-testid={`def-espacenet-fto-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-900 flex items-center gap-1.5">
                        {s.title}
                        <ExternalLink className="w-3 h-3 text-slate-400" />
                      </p>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{s.rationale}</p>
                      {s.cql && (
                        <p className="mt-1.5 font-mono text-[10px] text-teal-700 bg-teal-50 px-1.5 py-1 rounded inline-block break-all">
                          {s.cql}
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
        {result.watchlist?.length > 0 && (
          <div>
            <h4 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Competitor portfolios to watch</h4>
            <div className="grid md:grid-cols-2 gap-2">
              {result.watchlist.map((w, i) => (
                <a key={i} href={w.url} target="_blank" rel="noopener noreferrer" className="block border rounded-lg p-3 hover:border-amber-400 hover:shadow-sm transition-all bg-white" data-testid={`def-espacenet-watch-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-900 flex items-center gap-1.5">
                        {w.owner}
                        <ExternalLink className="w-3 h-3 text-slate-400" />
                      </p>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{w.why}</p>
                      {w.cpc_hint && <p className="text-[10px] text-amber-700 mt-1 font-mono">CPC: {w.cpc_hint}</p>}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- IP Map Table ----
const IPMSel = ({ value, onChange, options, testid }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger className="h-8 text-[11px] border-0 focus-visible:ring-1" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
  </Select>
);

function IpMapTable({ data, productData, setData, persistTab }) {
  const [busy, setBusy] = useState(false);
  const productComponents = (productData?.component_map || []).filter((r) => (r.component || "").trim()).map((r) => r.component);
  const rows = (data.ip_map && data.ip_map.length > 0)
    ? data.ip_map
    : (productComponents.length > 0 ? productComponents : ["Lead asset", "Platform", "Data / datasets", "Assays / models", "Manufacturing process"]).map((c, i) => ({ id: `ip_seed_${i}`, component: c, why_matters: "", protection_type: "", ownership_control: "", fto_risk: "", disclosure_risk: "", action_needed: "" }));

  const setRows = (n) => { const next = { ...data, ip_map: n }; setData(next); persistTab(next); };
  const setRowsNoPersist = (n) => setData({ ...data, ip_map: n });
  const updateCell = (id, patch) => setRowsNoPersist(rows.map((r) => r.id === id ? { ...r, ...patch } : r));
  const commit = () => setRows(rows);
  const addRow = () => setRows([...rows, { id: `ip_${Date.now()}`, component: "", why_matters: "", protection_type: "", ownership_control: "", fto_risk: "", disclosure_risk: "", action_needed: "" }]);
  const removeRow = (id) => setRows(rows.filter((r) => r.id !== id));
  const aiFill = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/technology/suggest-table`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ table: "ip_map", rows, context: { safe_disclose: data.safe_disclose, confidential: data.confidential, product_component_map: productData?.component_map || [] } }) });
      if (res.ok) { const d = await res.json(); if (d.rows?.length) { setRows(d.rows); toast.success("IP map filled"); } else toast.info("No rows"); }
      else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  const commitAfter = (fn) => { fn(); setTimeout(commit, 0); };
  const riskColor = (v) => v === "High" ? "text-red-600 font-medium" : v === "Medium" ? "text-amber-600" : v === "Low" ? "text-emerald-600" : "text-slate-500";

  return (
    <div data-testid="def-ip-map">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-500">Map each critical component protection, ownership, FTO risk and disclosure risk.</p>
        <Button size="sm" variant="ghost" onClick={aiFill} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="def-ip-ai-fill">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI fill</span>
        </Button>
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <div className="min-w-[1100px]">
          <div className="grid gap-0 bg-slate-50 border-b text-[10px] font-medium text-slate-500 uppercase tracking-wide" style={{ gridTemplateColumns: "3fr 3fr 2.5fr 2fr 1.5fr 1.5fr 3fr 0.5fr" }}>
            <div className="px-2 py-2 border-r">Component</div>
            <div className="px-2 py-2 border-r">Why it matters</div>
            <div className="px-2 py-2 border-r">Protection type</div>
            <div className="px-2 py-2 border-r">Ownership / control</div>
            <div className="px-2 py-2 border-r">FTO risk</div>
            <div className="px-2 py-2 border-r">Disclosure risk</div>
            <div className="px-2 py-2 border-r">Action needed</div>
            <div className="px-2 py-2"></div>
          </div>
          {rows.map((row) => (
            <div key={row.id} className="grid gap-0 border-b last:border-b-0 hover:bg-slate-50/40" style={{ gridTemplateColumns: "3fr 3fr 2.5fr 2fr 1.5fr 1.5fr 3fr 0.5fr" }} data-testid={`def-ip-row-${row.id}`}>
              <div className="border-r p-1"><Textarea value={row.component} onChange={(e) => updateCell(row.id, { component: e.target.value })} onBlur={commit} rows={2} className="text-xs border-0 focus-visible:ring-1 resize-none min-h-[3rem]" data-testid={`def-ip-comp-${row.id}`} /></div>
              <div className="border-r p-1"><Textarea value={row.why_matters} onChange={(e) => updateCell(row.id, { why_matters: e.target.value })} onBlur={commit} rows={2} className="text-xs border-0 focus-visible:ring-1 resize-none min-h-[3rem]" data-testid={`def-ip-why-${row.id}`} /></div>
              <div className="border-r p-1"><IPMSel value={row.protection_type} onChange={(v) => commitAfter(() => updateCell(row.id, { protection_type: v }))} options={PROTECTION_TYPES} testid={`def-ip-prot-${row.id}`} /></div>
              <div className="border-r p-1"><IPMSel value={row.ownership_control} onChange={(v) => commitAfter(() => updateCell(row.id, { ownership_control: v }))} options={OWNERSHIP_CONTROL} testid={`def-ip-own-${row.id}`} /></div>
              <div className={`border-r p-2 text-xs ${riskColor(row.fto_risk)}`}><IPMSel value={row.fto_risk} onChange={(v) => commitAfter(() => updateCell(row.id, { fto_risk: v }))} options={RISK_LEVELS} testid={`def-ip-fto-${row.id}`} /></div>
              <div className={`border-r p-2 text-xs ${riskColor(row.disclosure_risk)}`}><IPMSel value={row.disclosure_risk} onChange={(v) => commitAfter(() => updateCell(row.id, { disclosure_risk: v }))} options={RISK_LEVELS} testid={`def-ip-disc-${row.id}`} /></div>
              <div className="border-r p-1"><Textarea value={row.action_needed} onChange={(e) => updateCell(row.id, { action_needed: e.target.value })} onBlur={commit} rows={2} className="text-xs border-0 focus-visible:ring-1 resize-none min-h-[3rem]" data-testid={`def-ip-act-${row.id}`} /></div>
              <div className="flex items-start justify-center pt-2"><button onClick={() => removeRow(row.id)} className="text-slate-300 hover:text-red-500" data-testid={`def-ip-del-${row.id}`}><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
          ))}
        </div>
      </div>
      <Button size="sm" variant="ghost" onClick={addRow} className="mt-2 text-teal-600" data-testid="def-ip-add"><PlusCircle className="w-4 h-4 mr-1" /> Add component</Button>
    </div>
  );
}

// (Defensibility Scores Table removed — the whole "Defensibility score" section was retired.)

export default function DefensibilityTab({ data, setData, persist, productData }) {
  const set = (patch) => setData({ ...data, ...patch });
  const setField = (k) => (v) => set({ [k]: v });
  const persistTab = (nextDef) => persist({ defensibility: nextDef });

  const [gBusy, setGBusy] = useState(false);
  const checkGuidance = async () => {
    setGBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/technology/check-guidance`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ tab: "defensibility", tab_data: data }) });
      if (res.ok) { const d = await res.json(); const next = { ...data, guidance: d.warnings || [] }; setData(next); persistTab(next); if (d.warnings?.length) toast.success(`${d.warnings.length} coach warnings`); else toast.info("No warnings"); }
      else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setGBusy(false);
  };
  const dismissWarn = (id) => { const next = { ...data, guidance: (data.guidance || []).filter((w) => w.id !== id) }; setData(next); persistTab(next); };

  const resetTab = () => {
    if (window.confirm("Reset the Defensibility tab? Clears all answers, tables and questions.")) {
      const empty = { ip_map: [], scores: [], safe_disclose: "", confidential: "", needs_filing: "", collab_ownership: "", expert_questions: "", key_questions: [], guidance: [] };
      setData(empty); persistTab(empty); toast.success("Defensibility reset");
    }
  };

  const ctx = { safe_disclose: data.safe_disclose, confidential: data.confidential, needs_filing: data.needs_filing };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="defensibility-tab">
      <div className="lg:col-span-2 space-y-4">
        <EspacenetSection data={data} />

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Shield className="w-4 h-4 text-teal-600" /> IP and control map</CardTitle>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={checkGuidance} disabled={gBusy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="def-guidance-btn">
                  {gBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">Coach check</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={resetTab} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="def-reset-btn"><RotateCcw className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">Rows seed from your Product component map. Fill each row protection, ownership, FTO, disclosure risk and next action.</p>
          </CardHeader>
          <CardContent><IpMapTable data={data} productData={productData} setData={setData} persistTab={persistTab} /></CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-800 flex items-center gap-2"><EyeOff className="w-4 h-4 text-teal-600" /> Disclosure and collaboration risk — coach review</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <WandField tab="defensibility" field="safe_disclose" label="What can we safely disclose?" value={data.safe_disclose} onChange={setField("safe_disclose")} onBlur={persist} placeholder="What can be shared in public decks, mentor sessions or non-confidential conversations?" context={ctx} testid="def-safe" />
            <WandField tab="defensibility" field="confidential" label="What should remain confidential?" value={data.confidential} onChange={setField("confidential")} onBlur={persist} placeholder="What should not be disclosed without NDA or IP advice?" context={ctx} testid="def-conf" />
            <WandField tab="defensibility" field="needs_filing" label="What needs filing or protection before disclosure?" value={data.needs_filing} onChange={setField("needs_filing")} onBlur={persist} placeholder="e.g. target, molecule, sequence, assay, method, dataset, patient-selection approach." context={ctx} testid="def-filing" />
            <WandField tab="defensibility" field="collab_ownership" label="Where could collaboration create ownership issues?" value={data.collab_ownership} onChange={setField("collab_ownership")} onBlur={persist} placeholder="e.g. data generated by CROs, CDMOs, academic labs, pharma partners or platform collaborators." context={ctx} testid="def-collab" />
            <WandField tab="defensibility" field="expert_questions" label="What are the top IP questions for an expert?" value={data.expert_questions} onChange={setField("expert_questions")} onBlur={persist} placeholder="List the questions you need to ask an IP or BD expert." context={ctx} testid="def-expert" />
          </CardContent>
        </Card>

        <GuidancePanel warnings={data.guidance || []} onDismiss={dismissWarn} onRefresh={checkGuidance} busy={gBusy} />
      </div>
      <div className="lg:col-span-1"><CautionaryTale tale={CAUTIONARY_TALES.defensibility} testid="def-cautionary" /></div>
    </div>
  );
}
